import { pushIfNotExists } from "helpers/functions";
import { CustomJobPhase } from "hooks/jobs/job-phases/useGetJobPhase";
import { CustomJobPhaseCostItem } from "hooks/jobs/job-phases/useGetJobPhaseCostItem";
import * as z from "zod";

export const schema = z.object({
  jobPhaseId: z.string().min(1, "Job phase id is required"),
  date: z.string().min(1, "Date is Required"),
  materialNeeded: z.coerce.number().min(0).optional(),
  plants: z.array(
    z.object({
      plantId: z.string().min(1, "Plant Id is Required"),
      plantProductId: z.string().min(1, "Plant Product Id is Required"),
      quantity: z.coerce.number().min(0),
      rate: z.coerce.string().min(1, "Rate is Required"),
    })
  ),
  materialsNoPlants: z.array(
    z.object({
      name: z.string().min(1, "Material name is required"),
      quantity: z.coerce.number().min(0),
      rate: z.coerce.string().min(1, "Rate is Required"),
    })
  ),
  equipmentPieces: z.array(
    z.object({
      id: z.string().optional(),
      name: z.string().optional(),
      quantity: z.coerce.number().min(0),
      rate: z.coerce.number().min(0),
      timeOnjob: z.coerce.number().min(0),
      timeTravel: z.coerce.number().min(0),
    })
  ),
  additionalCosts: z.array(
    z.object({
      id: z.string().min(1, "Additional Cost Id is Required"),
      name: z.string().optional(),
      cost: z.coerce.number().min(0),
      feeAmount: z.coerce.number().min(0),
      feeType: z.string().optional(),
      isSubcontractorCost: z.boolean().optional(),
      costTotal: z.coerce.number().optional().or(z.null()),
    })
  ),
  employees: z.array(
    z.object({
      id: z.string().min(1, "Employee Id is Required"),
      name: z.string().optional(),
      rate: z.coerce.number().min(0),
      timeOnjob: z.coerce.number().min(0),
      timeTravel: z.coerce.number().min(0),
    })
  ),
  transferTrucks: z.array(
    z.object({
      id: z.string().min(1, "Plant Transfer Truck Id is Required"),
      name: z.string().optional(),
      tonnage: z.coerce.number().min(0),
      rateHourly: z.coerce.number().min(0),
      rateTon: z.coerce.number().min(0),
      hours: z.coerce.number().min(0),
    })
  ),
});

export type JobPhaseCostItemInputSchema = z.infer<typeof schema>;

export function cleanAddJobPhaseCostsInput(values: JobPhaseCostItemInputSchema) {
  const cleanParams = {
    ...values,
    plants: values.plants.map((p: any) => ({
      plantId: p.plantId,
      plantProductId: p.plantProductId,
      quantity: p.quantity,
      rate: p.rate,
    })),
    materialsNoPlants: values.materialsNoPlants.map((m: any) => ({
      name: m.name,
      quantity: m.quantity,
      rate: m.rate,
    })),
    employees: values.employees.map((e) => ({
      id: e.id,
      timeOnjob: e.timeOnjob,
      timeTravel: e.timeTravel,
    })),
    equipmentPieces: values.equipmentPieces.map((e) => ({
      id: e.id,
      quantity: Number(e.quantity ?? 0),
      timeOnjob: Number(e.timeOnjob ?? 0),
      timeTravel: Number(e.timeTravel ?? 0),
    })),
    additionalCosts: values.additionalCosts.map((a) => ({
      id: a.id,
      feeAmount: a.feeAmount,
      isSubcontractorCost: Boolean(a.isSubcontractorCost),
    })),
    transferTrucks: values.transferTrucks.map((t) => ({
      id: t.id,
      hours: t.hours,
    })),
  };

  return cleanParams;
}

export const getDefaultValues = ({
  jobPhase,
  jobPhaseCostItem,
  date,
}: {
  jobPhase: CustomJobPhase;
  jobPhaseCostItem: CustomJobPhaseCostItem;
  date: string;
}) => {
  const equipmentPieces = jobPhaseCostItem?.equipmentPieces?.length
    ? pushIfNotExists(
        jobPhaseCostItem?.equipmentPieces,
        jobPhase?.workOrderEquipmentPieces ?? [],
        "equipmentPieceId"
      )
    : jobPhase?.defaultEquipmentPieces;
  const employees = jobPhaseCostItem?.employees ?? jobPhase?.defaultEmployees;
  const additionalCosts = jobPhaseCostItem?.additionalCosts ?? jobPhase?.defaultAdditionalCosts;
  const transferTrucks = pushIfNotExists(
    jobPhaseCostItem?.transferTrucks ?? [],
    jobPhase?.workOrderTransferTrucks ?? [],
    "transferTruckId"
  );

  const mapCostItemPlant = (plant) => ({
    plantId: plant.plantId ?? "",
    plantProductId: plant.plantProductId ?? "",
    quantity: plant.quantity ?? 0,
    rate: plant.rate ?? "0",
  });

  const mapJobPhasePlant = (jobPhasePlant) => {
    const { plant, rate, plantProduct, materialNeeded } = jobPhasePlant;
    return {
      plantId: plant?.id ?? "",
      plantProductId: plantProduct?.id ?? "",
      quantity: materialNeeded ?? 0,
      rate: String(rate ?? 0),
    };
  };

  const costItemPlants = jobPhaseCostItem?.plants;
  const jobPhasePlants = jobPhase?.jobPhasePlants;

  const plants = costItemPlants?.length
    ? costItemPlants.map(mapCostItemPlant)
    : jobPhasePlants?.map(mapJobPhasePlant) ?? [];

  const costItemMaterialsNoPlants = jobPhaseCostItem?.materialsNoPlants;
  const jobPhaseMaterialsNoPlants = jobPhase?.jobPhaseMaterialNoPlants;

  const materialsNoPlants = costItemMaterialsNoPlants?.length
    ? costItemMaterialsNoPlants.map((m) => ({
        name: m.name ?? "",
        quantity: m.quantity ?? 0,
        rate: m.rate ?? "0",
      }))
    : jobPhaseMaterialsNoPlants?.map((m) => ({
        name: m.name ?? "",
        quantity: m.quantity ?? 0,
        rate: String(m.rate ?? 0),
      })) ?? [];

  return {
    date,
    jobPhaseId: jobPhase.id,
    materialNeeded: jobPhaseCostItem?.material?.materialNeeded ?? +jobPhase.proposedMaterialNeeded,
    plants,
    materialsNoPlants,
    equipmentPieces: equipmentPieces.map((equipmentPiece) => ({
      id: equipmentPiece.equipmentPieceId ?? equipmentPiece.id,
      name: equipmentPiece.name,
      quantity: equipmentPiece.quantity,
      timeTravel: equipmentPiece.timeTravel,
      timeOnjob: equipmentPiece.timeOnjob,
      rate: equipmentPiece.rate,
    })),
    additionalCosts:
      additionalCosts?.map((additionalCost) => ({
        id: additionalCost?.additionalCostId ?? additionalCost?.id,
        name: additionalCost.name,
        cost: additionalCost.cost,
        quantity: additionalCost?.quantity ?? 0,
        costTotal: additionalCost.costTotal,
        feeAmount: additionalCost.feeAmount ?? 0,
        feeType: additionalCost.feeType,
        isSubcontractorCost: additionalCost.isSubcontractorCost ?? false,
      })) ?? [],
    employees:
      employees?.map((employee) => ({
        id: employee.employeeId ?? employee.id,
        name: employee.name,
        rate: employee.rate,
        timeOnjob: employee.timeOnjob,
        timeTravel: employee.timeTravel,
      })) ?? [],
    transferTrucks:
      transferTrucks?.map((jobPhasePlantTransferTruck) => ({
        id: jobPhasePlantTransferTruck?.transferTruckId ?? jobPhasePlantTransferTruck?.id,
        name: jobPhasePlantTransferTruck.name,
        tonnage: jobPhasePlantTransferTruck.tonnage,
        rateHourly: jobPhasePlantTransferTruck.rateHourly,
        rateTon: jobPhasePlantTransferTruck.rateTon,
        hours: jobPhasePlantTransferTruck?.hours,
      })) ?? [],
  };
};

// import { JobPhase, JobPhaseCostItem } from "generated/graphql";
// import * as z from "zod";

// export const JobPhaseCostItemInputResolver = z.object({
//   /* end local values */
//   jobPhaseId: z.string().min(1, "Job phase id is required"),
//   plantId: z.string(),
//   plantProductId: z.string(),
//   materialNeeded: z.literal("").or(z.number()),
//   equipmentPieces: z.array(
//     z.object({
//       id: z.string().min(1, "Equipment Piece Id is Required"),
//       name: z.string().optional(),
//       quantity: z.number(),
//       rate: z.number(),
//       timeOnjob: z.number(),
//       timeTravel: z.number(),
//     })
//   ),
//   additionalCosts: z.array(
//     z.object({
//       id: z.string().min(1, "Additional Cost Id is Required"),
//       name: z.string().optional(),
//       cost: z.number(),
//       feeAmount: z.number(),
//     })
//   ),
//   employees: z.array(
//     z.object({
//       id: z.string().min(1, "Employee Id is Required"),
//       name: z.string().optional(),
//       rate: z.number(),
//       timeOnjob: z.number(),
//       timeTravel: z.number(),
//     })
//   ),
//   transferTrucks: z.array(
//     z.object({
//       id: z.string().min(1, "Plant Transfer Truck Id is Required"),
//       name: z.string().optional(),
//       tonnage: z.number(),
//       rateHourly: z.number(),
//       rateTon: z.number(),
//       hours: z.number(),
//     })
//   ),
// });

// export type JobPhaseCostItemInputSchema = z.infer<typeof JobPhaseCostItemInputResolver>;

// export const getDefaultValues = ({
//   jobPhase,
//   jobPhaseCostItem,
// }: {
//   jobPhase: JobPhase;
//   jobPhaseCostItem: JobPhaseCostItem;
// }): JobPhaseCostItemInputSchema => {
//   const equipmentPieces = jobPhaseCostItem?.equipmentPieces ?? jobPhase?.defaultEquipmentPieces;
//   const employees = jobPhaseCostItem?.employees ?? jobPhase?.defaultEmployees;

//   return {
//     jobPhaseId: jobPhase.id,
//     plantId: jobPhaseCostItem?.plantId ?? null,
//     plantProductId: jobPhaseCostItem?.plantProductId ?? null,
//     materialNeeded: jobPhase.proposedMaterialNeeded ?? 0,
//     equipmentPieces: equipmentPieces.map((equipmentPiece) => ({
//       id: equipmentPiece?.equipmentPieceId ?? equipmentPiece?.id,
//       name: equipmentPiece.name,
//       quantity: equipmentPiece.quantity ?? 1,
//       rate: equipmentPiece.rate,
//       timeTravel: 0,
//       timeOnjob: 0,
//     })),
//     additionalCosts:
//       jobPhaseCostItem?.additionalCosts?.map((additionalCost) => ({
//         id: additionalCost?.additionalCostId,
//         name: additionalCost?.name,
//         cost: 0,
//         feeAmount: 0,
//       })) ?? [],
//     employees:
//       employees?.map((employee) => ({
//         id: employee?.employeeId ?? employee.id,
//         name: employee.name,
//         rate: employee.rate,
//         timeOnjob: 0,
//         timeTravel: 0,
//       })) ?? [],
//     transferTrucks:
//       jobPhaseCostItem?.transferTrucks?.map((jobPhasePlantTransferTruck) => {
//         return {
//           id: jobPhasePlantTransferTruck?.plantTransferTruckId,
//           name: jobPhasePlantTransferTruck?.name,
//           tonnage: jobPhasePlantTransferTruck?.tonnage,
//           rateHourly: jobPhasePlantTransferTruck?.rateHourly,
//           rateTon: jobPhasePlantTransferTruck?.rateTon,
//           hours: 0,
//         };
//       }) ?? [],
//   };
// };
